import { CVE_LINK } from "@constants/index";
import useEscapeKey from "@hooks/useEscapeKey";
import { useTheme } from "@hooks/useTheme";
import { uppercase } from "@utils/common";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TableWithPagination from "../Tables/TableWithPagination";

const CveMergedModal = ({ show, onClose, data, "data-testid": dataTestId = "cve-merged-modal" }) => {
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [mergedData, setMergedData] = useState([]);
  useEscapeKey(onClose, show);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const formattedData = data.map((cveId) => ({ cve: cveId }));
      setMergedData(formattedData);
    }
  }, [data]);

  if (!show) {
    return null;
  }

  const columns = ["cve"];

  const columnConfig = {
    cve: {
      style: {
        width: "30%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("status.cve"),
      customRender: (value, row) => (
        <a
          href={`${CVE_LINK}/${row.cve}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`${colors.textComparisonLink} ${colors.hoverTextComparisonLink} underline`}
        >
          {row.cve}
        </a>
      ),
    },
  };

  return (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-center`} data-testid={`${dataTestId}-overlay`}>
      {/* Backdrop without blur */}
      <div
        className={`border ${colors.borderPrimary} ${colors.bgModal} w-[20rem] max-h-full pb-6 flex-shrink-0 flex flex-col rounded-2xl shadow-xl bg-white/10 backdrop-blur-md`}
        onClick={(e) => e.stopPropagation()}
        data-testid={dataTestId}
      >
        {/* Header */}
        <div className={`flex items-center justify-between pt-6 pb-4 px-6`} data-testid={`${dataTestId}-header`}>
          <h3 className={`text-lg font-semibold text-white`} data-testid={`${dataTestId}-title`}>{uppercase(t("common.cve-merged-modal-title"))}</h3>
          <button
            data-testid={`${dataTestId}-close-button`}
            onClick={onClose}
            className={`text-white/80 transition-colors duration-200 hover:text-white hover:bg-white/10 p-1 rounded-full`}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col" data-testid={`${dataTestId}-content`}>
          <div className="px-6 flex-1">
            {!data || !Array.isArray(data) || data.length === 0 ? (
              <div className={`text-center py-4 text-white/80`} data-testid={fieldValueTestId("no-data")}>{t("common.no-data-to-display")}</div>
            ) : (
              <div className="overflow-hidden">
                <TableWithPagination
                  columns={columns}
                  data={mergedData}
                  columnConfig={columnConfig}
                  maxRowsPerPage={10}
                  currentPage={1}
                  setCurrentPage={() => {}}
                  totalPages={1}
                  totalData={mergedData.length}
                  showPagination={false}
                  tableStyle="component-table"
                  loading={false}
                  stickyHeader={true}
                  scrollY="350px"
                  dataTestId={`${dataTestId}-table`}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CveMergedModal;
